/**
 * Shared helpers for handling schema fields (serviceSchema, faqSchema, etc.)
 * that the API sometimes returns as a real object and sometimes as an
 * already-stringified (or even double-stringified) JSON string.
 *
 * Import this from both the page that fetches the service and the modal
 * that edits it, so there's only one place that defines "what counts as
 * a valid schema object."
 */

// Normalizes any shape (object, JSON string, or double-encoded JSON string)
// into a real JS object.
export const safeParseSchema = (value) => {
  if (value == null) return {};

  let current = value;
  let attempts = 0;

  // Keep parsing while it's a string that looks like JSON.
  // Handles single AND double-encoded strings.
  while (typeof current === "string" && attempts < 3) {
    const trimmed = current.trim();
    if (!trimmed) return {};

    try {
      current = JSON.parse(trimmed);
    } catch {
      // It's a string but not valid JSON — stop trying to parse further.
      break;
    }
    attempts += 1;
  }

  return typeof current === "object" && current !== null ? current : {};
};

// Pretty-print any schema value (object OR stringified JSON) for display
// in an editor (e.g. Monaco).
export const formatSchemaForEditor = (value) =>
  JSON.stringify(safeParseSchema(value), null, 2);

// Convenience: normalize all four schema fields on a schemaData-shaped object.
// Use this right after fetching from the API and right after a save
// response comes back, so schemaData never holds raw (possibly
// double-encoded) strings.
export const normalizeSchemaFields = (data = {}) => ({
  ...data,
  serviceSchema: safeParseSchema(data.serviceSchema),
  faqSchema: safeParseSchema(data.faqSchema),
  serviceSchema_ar: safeParseSchema(data.serviceSchema_ar),
  faqSchema_ar: safeParseSchema(data.faqSchema_ar),
});
